import { createElement } from '../../adapters/dom';
import { createCopyButton } from '../components/copy-button';
import { createCountdownElement } from '../components/countdown';
import { renderQrCode } from '../components/qr-code';
import { createStatusBadge } from '../components/status-badge';
import type {
  WidgetMethodSelectionResponse,
  WidgetPaymentStatusResponse
} from '../../types/state';

export interface PaymentDetailsTemplateOptions {
  selection: WidgetMethodSelectionResponse;
  status?: WidgetPaymentStatusResponse | null;
  onCancel: () => void;
}

function getRemainingMs(expiresAt: string): number {
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(expiresAt.trim());
  const isoLike = expiresAt.includes('T') ? expiresAt : expiresAt.replace(' ', 'T');
  const normalized = hasTimezone ? isoLike : `${isoLike}Z`;

  return Math.max(0, new Date(normalized).getTime() - Date.now());
}

function formatPaymentAmount(value: string | number, asset: string): string {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return `${value} ${asset}`;
  }

  const normalizedAsset = asset.toUpperCase();

  if (normalizedAsset === 'USDT' || normalizedAsset === 'USDC') {
    return `${numericValue.toFixed(2)} ${normalizedAsset}`;
  }

  if (normalizedAsset === 'SOL' || normalizedAsset === 'ETH' || normalizedAsset === 'TRX') {
    return `${numericValue.toFixed(6).replace(/\.?0+$/, '')} ${normalizedAsset}`;
  }

  return `${numericValue.toFixed(2)} ${normalizedAsset}`;
}

type PaymentQrMode = 'address' | 'payment_payload';

function getQrValue(selection: WidgetMethodSelectionResponse, mode: PaymentQrMode = 'address'): string {
  if (mode === 'payment_payload') {
    return selection.qrPayload;
  }

  return selection.address;
}

export async function createPaymentDetailsTemplate(
  options: PaymentDetailsTemplateOptions
): Promise<{
  element: HTMLDivElement;
  setRemainingMs: (remainingMs: number) => void;
}> {
  const wrapper = createElement('div', {
    className: 'cpw-screen cpw-screen-payment'
  }) as HTMLDivElement;

  const currentStatus = options.status?.status ?? options.selection.status;

  const title = createElement('h3', {
    className: 'cpw-title',
    textContent: 'Оплатите заказ'
  });

  const topRow = createElement('div', {
    className: 'cpw-payment-top-row'
  }) as HTMLDivElement;

  const badge = createStatusBadge(currentStatus);

  const networkAsset = createElement('div', {
    className: 'cpw-payment-network-asset',
    textContent: `${options.selection.asset} / ${options.selection.network}`
  });

  topRow.append(badge, networkAsset);

  const amountBlock = createElement('div', {
    className: 'cpw-amount-block'
  }) as HTMLDivElement;

  const amountLabel = createElement('div', {
    className: 'cpw-amount-label',
    textContent: 'Сумма к оплате'
  });

  const amountValue = createElement('div', {
    className: 'cpw-amount-value',
    textContent: formatPaymentAmount(options.selection.expectedAmount, options.selection.asset)
  });

  amountBlock.append(amountLabel, amountValue);

  const qrContainer = createElement('div', {
    className: 'cpw-qr-container'
  }) as HTMLDivElement;

  const qrImage = await renderQrCode({
    value: getQrValue(options.selection, 'address'),
    size: 220,
    alt: 'QR code with payment address'
  });

  qrContainer.append(qrImage);

  const addressBlock = createElement('div', {
    className: 'cpw-address-block'
  }) as HTMLDivElement;

  const addressLabel = createElement('div', {
    className: 'cpw-address-label',
    textContent: 'Адрес для оплаты'
  });

  const addressValue = createElement('div', {
    className: 'cpw-address-value',
    textContent: options.selection.address
  });

  const copyButton = createCopyButton({
    value: options.selection.address,
    label: 'Копировать'
  });

  const addressRow = createElement('div', {
    className: 'cpw-address-row'
  }) as HTMLDivElement;

  addressRow.append(addressValue, copyButton);
  addressBlock.append(addressLabel, addressRow);

  const confirmationsBlock = createElement('div', {
    className: 'cpw-confirmations-block'
  }) as HTMLDivElement;

  const confirmationsLabel = createElement('div', {
    className: 'cpw-confirmations-label',
    textContent: 'Подтверждения'
  });

  const currentConfirmations =
    options.status?.currentConfirmations ??
    options.selection.currentConfirmations ??
    0;

  const confirmationsRequired =
    options.status?.confirmationsRequired ??
    options.selection.confirmationsRequired ??
    0;

  const confirmationsValue = createElement('div', {
    className: 'cpw-confirmations-value',
    textContent: `${currentConfirmations} / ${confirmationsRequired}`
  });

  confirmationsBlock.append(confirmationsLabel, confirmationsValue);

  const countdown = createCountdownElement({
    label: 'Осталось времени',
    initialMs: getRemainingMs(options.status?.expiresAt ?? options.selection.expiresAt)
  });

  const actions = createElement('div', {
    className: 'cpw-actions'
  }) as HTMLDivElement;

  const cancelButton = createElement('button', {
    className: 'cpw-secondary-button',
    textContent: 'Отменить оплату',
    attributes: {
      type: 'button'
    }
  }) as HTMLButtonElement;

  cancelButton.addEventListener('click', options.onCancel);

  actions.append(cancelButton);

  wrapper.append(
    title,
    topRow,
    amountBlock,
    qrContainer,
    addressBlock,
    confirmationsBlock,
    countdown.element,
    actions
  );

  return {
    element: wrapper,
    setRemainingMs: countdown.setRemainingMs
  };
}